import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Box, useColorModeValue } from "@chakra-ui/react";

/**
 * GovMarkdown — the ONE renderer for every field this module names `*Markdown`
 * (motion body, rules, work brief, job steps, comment bodies). Until 2026-09-11
 * these rendered as `whiteSpace="pre-wrap"` plain text, so `**bold**` showed as
 * asterisks — the naming was aspirational. Tables scroll inside their own box
 * so a wide table never makes the page scroll sideways (390px rule).
 */
interface GovMarkdownProps {
  children?: string | null;
  fontSize?: string;
  color?: string;
  mt?: number | string;
}

export const GovMarkdown: React.FC<GovMarkdownProps> = ({ children, fontSize = "sm", color, mt }) => {
  const border = useColorModeValue("gray.200", "gray.600");
  const codeBg = useColorModeValue("gray.100", "gray.700");
  const quote = useColorModeValue("gray.600", "gray.400");
  if (!children) return null;
  return (
    <Box
      fontSize={fontSize}
      color={color}
      mt={mt}
      minW={0}
      className="gov-markdown"
      sx={{
        "& p": { my: 2 },
        "& p:first-of-type": { mt: 0 },
        "& ul, & ol": { pl: 6, my: 2 },
        "& li": { my: 0.5 },
        "& h1": { fontSize: "xl", fontWeight: "bold", mt: 4, mb: 2 },
        "& h2": { fontSize: "lg", fontWeight: "bold", mt: 3, mb: 2 },
        "& h3": { fontSize: "md", fontWeight: "bold", mt: 3, mb: 1 },
        "& h4, & h5, & h6": { fontWeight: "bold", mt: 2, mb: 1 },
        "& blockquote": { borderLeft: "3px solid", borderColor: border, pl: 3, color: quote, my: 2 },
        "& hr": { my: 3, borderColor: border },
        "& code": { bg: codeBg, px: 1, borderRadius: "sm", fontSize: "0.9em" },
        "& pre": { bg: codeBg, p: 3, borderRadius: "md", overflowX: "auto", my: 2 },
        "& pre code": { bg: "transparent", px: 0 },
        "& a": { color: "blue.500", textDecoration: "underline", wordBreak: "break-word" },
        "& .gov-md-table": { overflowX: "auto", my: 2, maxW: "100%" },
        "& table": { borderCollapse: "collapse", minW: "max-content" },
        "& th, & td": { border: "1px solid", borderColor: border, px: 2, py: 1, textAlign: "left" },
        "& th": { fontWeight: "bold" },
        "& img": { maxW: "100%" },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
          table: ({ node: _node, ...props }) => (
            <div className="gov-md-table">
              <table {...props} />
            </div>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
};

export default GovMarkdown;
